import { SearchType } from './enums';
import { parse } from './urlHelpers';

export const KEYWORD_KEYS = ['keywords', 'keyword', 'q'];
export const LOCATION_KEYS = [
  'address',
  'search',
  'bounds',
  'origin',
  'placeId',
  'mapSearch',
  'viewport',
  'mapCenter',
  'mapZoom',
  'lat',
  'lng',
  'neLat',
  'neLng',
  'swLat',
  'swLng',
];
const UI_STATE_QUERY_KEYS = ['mobilemenu', 'mobilesearch'];

const isObject = value => value !== null && typeof value === 'object';

const trimString = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
};

const normalizeSearchType = searchType => {
  return searchType === SearchType.KEYWORDS ? SearchType.KEYWORDS : SearchType.LOCATION;
};

const parseSearchQueryParams = currentSearch => {
  return parse(currentSearch || '', {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
};

const withRemovedPageAndUiState = queryParams => {
  const withoutPage = { ...queryParams, page: null };
  return UI_STATE_QUERY_KEYS.reduce((picked, key) => ({ ...picked, [key]: null }), withoutPage);
};

const withNullValuesForKeys = (queryParams, keys) => {
  return keys.reduce((picked, key) => ({ ...picked, [key]: null }), queryParams);
};

export const stripKeywordParams = params => {
  return withNullValuesForKeys(params, KEYWORD_KEYS);
};

export const stripLocationParams = params => {
  return withNullValuesForKeys(params, LOCATION_KEYS);
};

const hasValue = value => {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return true;
};

export const hasKeywordSearchParams = params => {
  return KEYWORD_KEYS.some(key => hasValue(params?.[key]));
};

export const hasLocationSearchParams = params => {
  return LOCATION_KEYS.some(key => hasValue(params?.[key]));
};

const locationParamsFromValue = locationValue => {
  if (!isObject(locationValue)) {
    return null;
  }

  const selectedPlace = isObject(locationValue.selectedPlace) ? locationValue.selectedPlace : {};
  const selectedAddress = trimString(selectedPlace.address);
  const typedAddress = trimString(locationValue.search);
  const address = selectedAddress || typedAddress || null;
  const bounds = selectedPlace.bounds || null;
  const origin = selectedPlace.origin || null;
  const placeId = selectedPlace.placeId || null;

  const hasLocationPayload = !!(address || bounds || origin || placeId);
  if (!hasLocationPayload) {
    return null;
  }

  return {
    // Keep `search` synced with address for location autocomplete compatibility.
    address,
    search: address,
    bounds,
    origin,
    placeId,
  };
};

/**
 * Merge current SearchPage URL query params with submitted search form values.
 *
 * Rules:
 * - preserve existing params (filters/location/sort/etc.)
 * - reset page to first page
 * - in keywords mode, update only `keywords`
 * - in location mode, update only location keys
 * - remove `keywords` when empty
 */
export const mergeSearchPageQueryParams = ({ currentSearch, values } = {}) => {
  const baseQueryParams = withRemovedPageAndUiState(parseSearchQueryParams(currentSearch));
  const normalizedValues = isObject(values) ? values : {};
  const searchType = normalizeSearchType(normalizedValues.searchType);

  if (searchType === SearchType.KEYWORDS) {
    const keywords = trimString(normalizedValues.keywords);
    const queryWithoutKeywordAndLocationParams = stripLocationParams(
      stripKeywordParams(baseQueryParams)
    );
    return {
      ...queryWithoutKeywordAndLocationParams,
      keywords: keywords || null,
    };
  }

  const queryWithoutKeywordParams = stripKeywordParams(baseQueryParams);
  const locationParams = locationParamsFromValue(normalizedValues.location);
  if (!locationParams) {
    return { ...queryWithoutKeywordParams };
  }

  return {
    ...stripLocationParams(queryWithoutKeywordParams),
    ...locationParams,
  };
};
